import { type ReactElement, type ReactNode, useMemo } from 'react'
import { cn } from '@util'
import { useLocation } from '@tanstack/react-router'
import { Box, Card, Loader, ScrollArea, Stack, Text } from '@mantine/core'
import { type MaterialSymbolType, Size, SizePrimitives, useIsManager, useProfile } from '@front/model'
import { isPageRouteVisible, type RouteScope } from './routeVisibility'
import { NavigationSkeleton, type SkeletonSection } from './NavigationSkeleton'
import { NavigationButton } from './NavigationButton'

export type NavbarCollapseProps =
  | {
      collapsible: true
      isOpen: boolean
      toggle: VoidFunction
    }
  | {
      collapsible?: false
    }

type Props = {
  routes: PageRoute[]
  toggleBurger?: VoidFunction
  variant?: 'default' | 'nested'
  sectionLabel?: string
  loading?: boolean
  isPending?: boolean
  header?: ReactNode
} & NavbarCollapseProps

export const Navigation = ({
  routes,
  toggleBurger,
  variant = 'default',
  sectionLabel = 'Navigation',
  loading,
  isPending,
  header,
  ...navbarCollapseProps
}: Props): ReactElement => {
  const currentPath = useLocation().pathname
  const visibleRoutes = useVisibleRoutes(routes, isPending)

  const handleClick = (): void => {
    if (toggleBurger) toggleBurger()
  }

  const isCollapsed = useMemo(() => {
    if (!navbarCollapseProps.collapsible) return false
    return !navbarCollapseProps.isOpen
  }, [navbarCollapseProps])

  if (variant === 'nested') {
    const groupedRoutes = groupRoutesBySection(visibleRoutes, sectionLabel)

    return (
      <Card withBorder className={cn('flex flex-col')} h='100%' mih={0} p={0}>
        {header && !isCollapsed && (
          <Box p='md' pb={0}>
            {header}
          </Box>
        )}
        <ScrollArea p={0} scrollbarSize={SizePrimitives['1']}>
          <Stack gap={isCollapsed ? 'xs' : 'md'} p={isCollapsed ? 'xs' : 'md'}>
            {isPending && <NavigationSkeleton isCollapsed={isCollapsed} sections={SKELETON_SECTIONS} />}
            {groupedRoutes.map((group) => (
              <Stack gap={Size.space.md} key={group.label}>
                {!isCollapsed && (
                  <Text size='xs' className={cn('px-2 font-medium tracking-[0.18em] uppercase')} c='dimmed'>
                    {group.label}
                  </Text>
                )}
                <Stack gap={6}>
                  {group.routes.map((route) => (
                    <NavigationButton
                      currentPath={currentPath}
                      isCollapsed={isCollapsed}
                      key={route.path}
                      onClick={handleClick}
                      {...route}
                    />
                  ))}
                </Stack>
              </Stack>
            ))}

            {loading && (
              <div className='flex items-center justify-center'>
                <Loader color='brand' size='sm' />
              </div>
            )}
          </Stack>
        </ScrollArea>
      </Card>
    )
  }

  return (
    <ScrollArea p={Size.space['3xl']} pb={Size.space.md}>
      <Stack className='relative' gap={5}>
        {isPending && <NavigationSkeleton isCollapsed={isCollapsed} sections={SKELETON_SECTIONS} />}
        {visibleRoutes.map((route) => (
          <NavigationButton
            currentPath={currentPath}
            isCollapsed={isCollapsed}
            key={route.path}
            onClick={handleClick}
            {...route}
          />
        ))}
      </Stack>
    </ScrollArea>
  )
}

type RouteSection = {
  label: string
  routes: PageRoute[]
}

const groupRoutesBySection = (routes: PageRoute[], fallbackLabel: string): RouteSection[] => {
  if (!routes.length) return []

  const hasSections = routes.some((route) => route.section)
  if (!hasSections) {
    return [{ label: fallbackLabel, routes }]
  }

  const grouped = new Map<string, PageRoute[]>()
  routes.forEach((route) => {
    const label = route.section ?? fallbackLabel
    grouped.set(label, [...(grouped.get(label) ?? []), route])
  })

  return [...grouped.entries()]
    .map(([label, groupedRoutes]) => ({
      label,
      routes: groupedRoutes,
    }))
    .sort((a, b) => navSectionOrder.indexOf(a.label) - navSectionOrder.indexOf(b.label))
}

export const useVisibleRoutes = (routes: PageRoute[], isPending = false): PageRoute[] => {
  const { isAdmin, isTantalim, hasPermission } = useProfile()
  const { isManager } = useIsManager()
  return useMemo(() => {
    if (isPending) return []
    return routes.filter((route) => isPageRouteVisible(route, { isAdmin, isManager, isTantalim, hasPermission }))
  }, [routes, isPending, isAdmin, isManager, isTantalim, hasPermission])
}

export const NAV_SECTIONS = ['My Work', 'People & Places', 'Performance', 'Platform', 'Internal'] as const

export type NavSection = (typeof NAV_SECTIONS)[number]

const navSectionOrder: readonly string[] = NAV_SECTIONS

const SKELETON_ROW_COUNTS = new Map<NavSection, number>([
  ['My Work', 4],
  ['People & Places', 4],
  ['Performance', 5],
  ['Platform', 6],
])

const SKELETON_SECTIONS: SkeletonSection[] = NAV_SECTIONS.flatMap((section) => {
  const rowCount = SKELETON_ROW_COUNTS.get(section)
  if (!rowCount) return []
  return [{ label: section, rowCount }]
})

export type PageRoute = {
  path: string
  label: string
  matchFormat?: 'exact' | 'starts'
  icon?: MaterialSymbolType
  section?: string
  scope?: RouteScope
  isExternalLink?: boolean
  hidden?: boolean
  rightSection?: ReactNode
  search?: true
}
